import axios from "axios";

const BASE_URL = process.env.VUE_APP_CALENDLY_API_BASE || "https://api.calendly.com";

function getAuthHeaders() {
  const token = process.env.VUE_APP_CALENDLY_TOKEN || "";
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

export const CalendlyAPI = {
  getToken() {
    return process.env.VUE_APP_CALENDLY_TOKEN || "";
  },

  /**
   * Fetch current authenticated user info
   */
  async getCurrentUser() {
    const headers = getAuthHeaders();
    const response = await axios.get(`${BASE_URL}/users/me`, { headers });
    return response.data ? response.data.resource : null;
  },

  /**
   * Fetch live user availability schedules from Calendly
   */
  async fetchUserSchedules(userUri) {
    const headers = getAuthHeaders();
    const url = userUri
      ? `${BASE_URL}/user_availability_schedules?user=${encodeURIComponent(userUri)}`
      : `${BASE_URL}/user_availability_schedules`;
    const response = await axios.get(url, { headers });
    return response.data && response.data.collection ? response.data.collection : [];
  },

  /**
   * Fetch live event types for user from Calendly
   */
  async fetchEventTypes(userUri) {
    const headers = getAuthHeaders();
    const url = userUri
      ? `${BASE_URL}/event_types?user=${encodeURIComponent(userUri)}`
      : `${BASE_URL}/event_types`;
    const response = await axios.get(url, { headers });
    return response.data && response.data.collection ? response.data.collection : [];
  },

  /**
   * Fetch specific availability schedule for an individual event type
   */
  async fetchEventTypeSchedule(eventTypeUri) {
    try {
      const headers = getAuthHeaders();
      const url = `${BASE_URL}/event_type_availability_schedules?event_type=${encodeURIComponent(
        eventTypeUri
      )}`;
      const response = await axios.get(url, { headers });
      if (
        response.data &&
        response.data.collection &&
        response.data.collection.length > 0
      ) {
        return response.data.collection[0].availability_rule;
      }
      return null;
    } catch (err) {
      console.warn("[Calendly] Error fetching event type schedule:", err.message);
      return null;
    }
  },

  /**
   * Helper: Format internal UI days state into Calendly v2 rules array
   */
  formatUiDaysToRules(days) {
    return days.map((day) => {
      let intervals = [];
      if (day.active && day.timeBlocks && day.timeBlocks.length > 0) {
        intervals = day.timeBlocks
          .filter((tb) => tb.start && tb.end)
          .map((tb) => ({
            from: tb.start,
            to: tb.end,
          }));
      }

      return {
        type: "wday",
        wday: day.key.toLowerCase(),
        intervals,
      };
    });
  },

  /**
   * Update an individual event type's availability schedule via PATCH /event_type_availability_schedules
   */
  async updateEventTypeSchedule(eventTypeUri, rules, timezone) {
    try {
      const headers = getAuthHeaders();
      const endpoint = `${BASE_URL}/event_type_availability_schedules`;
      const body = {
        event_type: eventTypeUri,
        availability_rule: {
          timezone: timezone || "America/Los_Angeles",
          rules: rules,
        },
      };
      const response = await axios.patch(endpoint, body, { headers });
      return { success: true, data: response.data };
    } catch (error) {
      let errorMessage = error.message;
      if (error.response && error.response.data) {
        const d = error.response.data;
        if (d.details && Array.isArray(d.details) && d.details.length > 0) {
          errorMessage = d.details
            .map((item) => (item.parameter ? `${item.parameter}: ${item.message}` : item.message))
            .join("; ");
        } else if (d.message) {
          errorMessage = d.message;
        } else if (d.title) {
          errorMessage = d.title;
        }
      }
      return {
        success: false,
        error: {
          message: errorMessage,
          raw: error.response ? error.response.data : null,
          status: error.response ? error.response.status : null,
        },
      };
    }
  },

  /**
   * Format payload for preview in confirmation modal
   */
  formatMultiMeetingPayload(targetMeetings, days, timezone) {
    const rules = this.formatUiDaysToRules(days);
    return targetMeetings.map((m) => ({
      meetingName: m.name,
      endpoint: "PATCH /event_type_availability_schedules",
      body: {
        event_type: m.uri || `https://api.calendly.com/event_types/${m.id}`,
        availability_rule: {
          timezone: timezone || "America/Los_Angeles",
          rules: rules,
        },
      },
    }));
  },

  /**
   * Batch update multiple meeting availability schedules
   */
  async updateMultipleSchedules(scheduleUpdates) {
    const results = [];
    for (const item of scheduleUpdates) {
      const res = await this.updateEventTypeSchedule(
        item.eventTypeUri,
        item.rules,
        item.timezone
      );
      results.push({
        meetingId: item.meetingId,
        meetingName: item.meetingName,
        success: res.success,
        data: res.data,
        error: res.error,
      });
    }
    return {
      success: results.every((r) => r.success),
      results,
    };
  },
};

export default CalendlyAPI;
